import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, siteImages } from '../data/mock';
import { Phone, Mail, MapPin, Clock, Send, Building2 } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'Contact Keto Vybe | Customer Support';
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-20">
        {/* Banner */}
        <section className="relative h-72 lg:h-80 overflow-hidden">
          <img 
            src={siteImages.contactBanner}
            alt="Contact"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className="text-vybe-400 font-semibold text-sm tracking-wider uppercase mb-3 block">Get In Touch</span>
              <h1 className="font-display text-4xl lg:text-5xl text-white mb-3">
                Contact Us
              </h1>
              <p className="text-slate-400 max-w-md mx-auto">
                Questions about our products? We're here to help.
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Contact Info */}
            <div className="space-y-4">
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="block bg-slate-800/50 rounded-2xl p-6 hover:bg-slate-800 transition-all border border-slate-700/50 group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-vybe-600/20 rounded-xl flex items-center justify-center group-hover:bg-vybe-600 transition-colors">
                    <Phone className="w-5 h-5 text-vybe-400 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Phone</h3>
                    <p className="text-vybe-400 font-medium">{CONTACT_INFO.phone}</p>
                  </div>
                </div>
              </a>

              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="block bg-slate-800/50 rounded-2xl p-6 hover:bg-slate-800 transition-all border border-slate-700/50 group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-600/20 rounded-xl flex items-center justify-center group-hover:bg-cyan-600 transition-colors">
                    <Mail className="w-5 h-5 text-cyan-400 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Email</h3>
                    <p className="text-cyan-400 font-medium">{CONTACT_INFO.email}</p>
                  </div>
                </div>
              </a>

              <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-coral-600/20 rounded-xl flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-coral-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Address</h3>
                    <p className="text-slate-400">{CONTACT_INFO.address.street}</p>
                    <p className="text-slate-400">{CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}</p>
                    <p className="text-slate-400">{CONTACT_INFO.address.country}</p>
                  </div>
                </div>
              </div>

              {/* Company */}
              <div className="bg-gradient-to-br from-vybe-600/20 to-vybe-800/20 rounded-2xl p-6 border border-vybe-500/30">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-vybe-600 rounded-xl flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Company</h3>
                    <p className="text-vybe-300 font-medium">{CONTACT_INFO.corpName}</p>
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-700 rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-slate-300" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white mb-3">Business Hours</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Mon - Fri</span>
                        <span className="text-white">9 AM - 6 PM</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Saturday</span>
                        <span className="text-white">10 AM - 4 PM</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Sunday</span>
                        <span className="text-slate-500">Closed</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-slate-800/50 rounded-3xl p-8 lg:p-10 border border-slate-700/50">
                <h2 className="font-display text-2xl text-white mb-2">Send Us a Message</h2>
                <p className="text-slate-400 mb-8">We'll respond within 24-48 hours.</p>

                {submitted ? (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 bg-vybe-600/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Send className="w-8 h-8 text-vybe-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
                    <p className="text-slate-400">Thank you for reaching out. We'll be in touch soon.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Name</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-5 py-4 bg-slate-900 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-vybe-500 focus:border-transparent transition-all"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-5 py-4 bg-slate-900 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-vybe-500 focus:border-transparent transition-all"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Subject</label>
                      <input
                        type="text"
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full px-5 py-4 bg-slate-900 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-vybe-500 focus:border-transparent transition-all"
                        placeholder="How can we help?"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Message</label>
                      <textarea
                        required
                        rows={5}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-5 py-4 bg-slate-900 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-vybe-500 focus:border-transparent transition-all resize-none"
                        placeholder="Tell us more..."
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-vybe-600 to-vybe-500 hover:from-vybe-500 hover:to-vybe-400 text-white font-bold rounded-xl transition-all disabled:opacity-50 shadow-button"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ContactPage;
